import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, X, ChevronRight } from "lucide-react";

/**
 * Discreet contextual banner pointing to a deep link inside the HTC tutorial.
 *
 * Usage in any wizard step (or anywhere in the app):
 *   <TutorialDeepLink
 *     chapter={2}
 *     section="section-2-2"
 *     id="wizard-upload"
 *     label="Comment importer un PDF ?"
 *     duration={8}
 *   />
 *
 * The dismissal is per-pointer (id) and persists in localStorage so the
 * user is never bothered twice. F1 still opens the full HTC.
 */
export default function TutorialDeepLink({
  chapter,
  section,
  id,
  label = "Voir le tutoriel",
  duration,
  className = "",
}) {
  const storageKey = "vtpTutorialTipsDismissed";
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const list = raw ? JSON.parse(raw) : [];
      if (Array.isArray(list) && list.includes(id)) setDismissed(true);
    } catch {
      /* ignore */
    }
  }, [id]);

  const dismiss = () => {
    setDismissed(true);
    try {
      const raw = localStorage.getItem(storageKey);
      const list = raw ? JSON.parse(raw) : [];
      const next = Array.isArray(list) ? Array.from(new Set([...list, id])) : [id];
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };

  if (dismissed) return null;

  const target = section
    ? `/tutorial/chapter/${chapter}#${section}`
    : `/tutorial/chapter/${chapter}`;

  return (
    <div
      role="note"
      data-testid={`tutorial-deeplink-${id}`}
      className={`mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-brand-gold/40 bg-brand-gold/10 px-4 py-2.5 text-sm shadow-sm ${className}`}
    >
      <BookOpen
        className="h-4 w-4 shrink-0 text-brand-gold"
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className="font-semibold text-foreground">{label}</span>
        {duration ? (
          <span className="ml-2 text-xs text-muted-foreground">
            · lecture {duration} min
          </span>
        ) : null}
      </span>
      <Link
        to={target}
        data-testid={`tutorial-deeplink-${id}-cta`}
        className="inline-flex items-center gap-1 rounded-md bg-brand-gold px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-background transition-colors hover:bg-brand-gold/90"
      >
        Voir le tutoriel
        <ChevronRight className="h-3 w-3" />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Masquer définitivement cette suggestion"
        data-testid={`tutorial-deeplink-${id}-dismiss`}
        className="rounded-full p-1 text-muted-foreground hover:bg-brand-gold/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
